import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NoticeService } from 'src/app/services/notice/notice.service';

export interface CMSNewsletterItem {
  id: string;
  title: string;
  dateStr: string;
  pdfUrl?: string;
  imageUrl?: string;
}

@Component({
  selector: 'app-newsletters',
  templateUrl: './newsletters.component.html',
  styleUrls: ['./newsletters.component.scss']
})
export class NewslettersComponent implements OnInit, OnDestroy {
  activeTab: string = 'Inter-College Quiz';
  cmsNewsletters: CMSNewsletterItem[] = [];
  isLoading = true;
  private sub: Subscription;

  constructor(private noticeService: NoticeService) { }

  ngOnInit(): void {
    this.sub = this.noticeService.getNewslettersList().snapshotChanges().subscribe((list) => {
      this.isLoading = false;
      const items = list.map((item) => {
        const val = item.payload.val() || {};
        const attachments = this.resolveAttachments(val);
        const dateInfo = this.parseDate(val.date || '', val.monthYear || '');
        
        let displayDate = 'Current';
        if (dateInfo.date || dateInfo.month || dateInfo.year) {
          const parts = [];
          if (dateInfo.date) parts.push(dateInfo.date);
          if (dateInfo.month) parts.push(dateInfo.month);
          if (dateInfo.year) parts.push(dateInfo.year);
          displayDate = parts.join(' ');
        }

        return {
          id: item.key,
          title: val.title || val.name || 'Untitled Newsletter',
          dateStr: displayDate,
          pdfUrl: attachments.pdfUrl,
          imageUrl: attachments.imageUrl
        };
      });
      this.cmsNewsletters = items.reverse();
    }, (error) => {
      this.isLoading = false;
      console.error('Error fetching newsletters:', error);
    });
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  isAttachmentURL(value: string, keyName: string): boolean {
    if (!value || typeof value !== 'string') return false;
    const valLower = value.toLowerCase().trim();
    return (
      valLower.startsWith('http://') ||
      valLower.startsWith('https://') ||
      valLower.startsWith('assets/') ||
      valLower.includes('firebasestorage') ||
      valLower.includes('/newsletters/') ||
      valLower.includes('/uploads/') ||
      valLower.includes('.pdf') ||
      valLower.includes('.png') ||
      valLower.includes('.jpg') ||
      valLower.includes('.jpeg') ||
      valLower.includes('.webp') ||
      valLower.includes('.gif') ||
      valLower.includes('.svg')
    );
  }

  resolveAttachments(val: any): { pdfUrl: string, imageUrl: string } {
    let pdfUrl = '';
    let imageUrl = '';

    if (val.pdfUrl && typeof val.pdfUrl === 'string') pdfUrl = val.pdfUrl.trim();
    else if (val.pdfURL && typeof val.pdfURL === 'string') pdfUrl = val.pdfURL.trim();
    else if (val.pdf && typeof val.pdf === 'string') pdfUrl = val.pdf.trim();

    if (val.imageUrl && typeof val.imageUrl === 'string') imageUrl = val.imageUrl.trim();
    else if (val.image && typeof val.image === 'string') imageUrl = val.image.trim();
    else if (val.img && typeof val.img === 'string') imageUrl = val.img.trim();
    else if (val.imageURL && typeof val.imageURL === 'string') imageUrl = val.imageURL.trim();
    else if (val.photo && typeof val.photo === 'string') imageUrl = val.photo.trim();
    else if (val.pic && typeof val.pic === 'string') imageUrl = val.pic.trim();

    for (const key in val) {
      if (val.hasOwnProperty(key) && typeof val[key] === 'string') {
        const valueStr = val[key].trim();
        if (this.isAttachmentURL(valueStr, key)) {
          const lowerKey = key.toLowerCase();
          const lowerValue = valueStr.toLowerCase();

          const hasPdfExt = lowerValue.includes('.pdf') || lowerValue.includes('.pdf?');
          const hasImgExt = lowerValue.includes('.png') || lowerValue.includes('.jpg') || lowerValue.includes('.jpeg') || lowerValue.includes('.webp') || lowerValue.includes('.gif') || lowerValue.includes('.svg');

          if (hasPdfExt) {
            if (!pdfUrl) pdfUrl = valueStr;
          } else if (hasImgExt) {
            if (!imageUrl) imageUrl = valueStr;
          } else {
            if (lowerKey.includes('pdf')) {
              if (!pdfUrl) pdfUrl = valueStr;
            } else if (lowerKey.includes('image') || lowerKey.includes('img') || lowerKey.includes('photo') || lowerKey.includes('pic')) {
              if (!imageUrl) imageUrl = valueStr;
            } else {
              if (!pdfUrl && !imageUrl) {
                pdfUrl = valueStr;
              }
            }
          }
        }
      }
    }
    return { pdfUrl, imageUrl };
  }

  parseDate(dateStr: string, monthYearStr?: string): { date: string, month: string, year: string } {
    if (!dateStr) {
      let month = '';
      let year = '';
      if (monthYearStr) {
        const parts = monthYearStr.trim().split(/\s+/);
        if (parts.length >= 2) { month = parts[0]; year = parts[1]; }
        else { month = monthYearStr; }
      }
      return { date: '', month, year };
    }

    const trimmed = dateStr.trim();
    if (trimmed.length <= 2) {
      let month = ''; let year = '';
      if (monthYearStr) {
        const parts = monthYearStr.trim().split(/\s+/);
        if (parts.length >= 2) { month = parts[0]; year = parts[1]; }
        else { month = monthYearStr; }
      }
      return { date: trimmed, month, year };
    }

    const parts = trimmed.split(/[-/]/);
    if (parts.length >= 3) {
      let day = parts[0].trim();
      let month = parts[1].trim();
      let year = parts[2].trim();

      if (day.length === 4) { const temp = day; day = year; year = temp; }
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      let monthName = month;
      const monthIdx = parseInt(month, 10);
      if (!isNaN(monthIdx) && monthIdx >= 1 && monthIdx <= 12) { monthName = monthNames[monthIdx - 1]; }
      return { date: day, month: monthName, year: year };
    }

    try {
      const d = new Date(trimmed);
      if (!isNaN(d.getTime())) {
        const day = String(d.getDate()).padStart(2, '0');
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthName = monthNames[d.getMonth()];
        const year = String(d.getFullYear());
        return { date: day, month: monthName, year: year };
      }
    } catch (e) {}

    return { date: trimmed, month: monthYearStr || '', year: '' };
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}